import {Fragment, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  Menu,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import {ArrowForwardIosSharp, Close, FilterAltOutlined} from '@mui/icons-material';
import {Option} from '../select-dropdown/select-dropdown';
import {CustomCheckbox} from '../checkbox/checkbox';
import {useEntity} from '../../contexts/entity/context';
import {useObserveViewport} from '../../hooks/observe-viewport';
import {useFetchShifts} from '../../hooks/use-fetch-shifts';
import {UserFilterTypes} from './users-filters-utils';
import {getLocalizedFieldValue} from '../../utils/functions/get-localized-field-value';
import {useFetchActivitiesSectorsAndGroups} from '../../screens/private/office/professional-caregiver/planner/calendar/shared/use-fetch-activities-sectors-and-groups';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../utils/constants/hardcoded';
import {useAuth} from '../../contexts/auth/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {ScreenPaths} from '../../enums/screen-path';

interface UserFiltersProps {
  hideActiveStatus?: boolean;
  hideSocialResponses?: boolean;
  hideShifts?: boolean;
  hideTaskGroups?: boolean;
  hideOccurrences?: boolean;
  taskStatus?: Option[];
  onChange: (filtersString: string) => void;
}

const initialFiltersState = (defaults?: {
  taskStatus?: Option[];
  activeStatus?: Option[];
}): {[K in UserFilterTypes]: Option[]} => ({
  socialResponses: [],
  activeStatus: defaults?.activeStatus ?? [],
  taskStatus: defaults?.taskStatus ?? [],
  shifts: [],
  patientOccurrences: [],
  generalOccurrences: [],
  badl: [],
  iadl: [],
  clinicalSupport: [],
  personalDevelopment: [],
});

const CHIPS_TEXT_MAX_LIMIT = {
  firstTwo: 16,
  all: 9,
};

export const UsersFilters = ({
  hideActiveStatus = false,
  hideSocialResponses = false,
  hideShifts = false,
  hideTaskGroups = false,
  hideOccurrences = false,
  taskStatus,
  onChange,
}: UserFiltersProps) => {
  const {t} = useTranslation();
  const {hasWritePermission} = useAuth();
  const taskStatusFilters = useMemo(() => taskStatus ?? [], [taskStatus]);
  const activeStatusFilters = useMemo(
    () =>
      hideActiveStatus
        ? undefined
        : [
            {
              key: 'true',
              label: t(translations.office.users_filters.sections.activeStatus.options.active),
            },
            {
              key: 'false',
              label: t(translations.office.users_filters.sections.activeStatus.options.inactive),
            },
          ],
    [hideActiveStatus, t]
  );
  const [filters, setFilters] = useState<Record<UserFilterTypes, Option[]>>(() =>
    initialFiltersState({taskStatus: taskStatusFilters, activeStatus: activeStatusFilters})
  );
  const [pendingSelected, setPendingSelected] =
    useState<Record<UserFilterTypes, Option[]>>(initialFiltersState);
  const [selected, setSelected] = useState<Record<UserFilterTypes, Option[]>>(initialFiltersState);
  const [dynamicTitles, setDynamicTitles] = useState<Partial<Record<UserFilterTypes, string>>>({
    badl: '',
    iadl: '',
    clinicalSupport: '',
    personalDevelopment: '',
  });
  const [isLoading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const filterMenuOpen = Boolean(anchorEl);
  const {search} = useLocation();
  const {isRunningCloseToMobile} = useObserveViewport();
  const {state: entityState} = useEntity();
  const {items: shiftsData} = useFetchShifts({enabled: !hideShifts});
  const {items: activitiesSectorsAndGroups, isLoading: activitiesIsLoading} =
    useFetchActivitiesSectorsAndGroups({
      enabled: !hideTaskGroups,
    });
  const occurrencesTypes = useMemo(
    () => (hideOccurrences ? [] : entityState.occurrencesTypes),
    [entityState.occurrencesTypes, hideOccurrences]
  );
  const socialResponseFilterOptions = useMemo<Option[]>(() => {
    if (hideSocialResponses) return [];

    return entityState.userEntitySocialResponses.map((socialResponse) => ({
      key: socialResponse.id,
      label: getLocalizedFieldValue(socialResponse, 'abbreviation'),
    }));
  }, [entityState.userEntitySocialResponses, hideSocialResponses]);

  const assembleFilterParams = (selectedOpts: Record<UserFilterTypes, Option[]>) => {
    const activationStates = selectedOpts.activeStatus.map((opt) => ['activationStates', opt.key]);

    const socialResponsesIds = selectedOpts.socialResponses.map((opt) => [
      'socialResponsesIds',
      opt.key,
    ]);

    const taskStatus = selectedOpts.taskStatus.map((opt) => ['taskStatus', opt.key]);

    const shifts = selectedOpts.shifts.map((opt) => ['shifts', opt.key]);

    const patientOccurrences = selectedOpts.patientOccurrences.map((opt) => [
      'patientOccurrences',
      opt.key,
    ]);
    const generalOccurrences = selectedOpts.generalOccurrences.map((opt) => [
      'generalOccurrences',
      opt.key,
    ]);

    const badl = selectedOpts.badl.map((opt) => ['badl', opt.key]);

    const iadl = selectedOpts.iadl.map((opt) => ['iadl', opt.key]);

    const clinicalSupport = selectedOpts.clinicalSupport.map((opt) => ['clinicalSupport', opt.key]);

    const personalDevelopment = selectedOpts.personalDevelopment.map((opt) => [
      'personalDevelopment',
      opt.key,
    ]);

    const filtersArr = activationStates
      .concat(socialResponsesIds)
      .concat(taskStatus)
      .concat(patientOccurrences)
      .concat(generalOccurrences)
      .concat(shifts)
      .concat(badl)
      .concat(iadl)
      .concat(clinicalSupport)
      .concat(personalDevelopment);

    return new URLSearchParams(filtersArr).toString();
  };

  const resetPendingFilters = () => setPendingSelected(selected);

  const openFiltersMenu = (event: any) => {
    // Set menu anchor element
    setAnchorEl(event.currentTarget);

    // Copy selected to the pending state
    setPendingSelected({...selected});
  };

  const closeFilterMenu = () => {
    // Remove menu anchor element
    setAnchorEl(null);

    // Reset pending filters state
    resetPendingFilters();
  };

  const clearFilters = () => {
    // Reset selected filters
    setSelected(initialFiltersState());

    // Reset pending filters
    setPendingSelected(initialFiltersState());

    // Arrange query parameters as an encoded URL string
    const filterParams = assembleFilterParams(initialFiltersState());

    // Dispatch encoded string
    onChange(filterParams);
  };

  const applyFilters = () => {
    // Commit selected to state
    setSelected({...pendingSelected});

    // Arrange query parameters as an encoded URL string
    const filterParams = assembleFilterParams({...pendingSelected});

    // Dispatch encoded string
    onChange(filterParams);

    // Close filters menu after applying
    closeFilterMenu();
  };

  const handleDeleteChip = (id: string) => {
    const updatedSelected: Record<UserFilterTypes, Option[]> = initialFiltersState();

    // Search selected and filter out sections per the passed id
    Object.entries(selected).forEach(([section, options]) => {
      return (updatedSelected[section as UserFilterTypes] =
        [...options.filter((option) => option.key !== id)] || []);
    });

    // Commit to state the filtered selection
    setSelected(updatedSelected);

    // Arrange query parameters as an encoded URL string
    const filterParams = assembleFilterParams(updatedSelected);

    // Dispatch encoded string
    onChange(filterParams);
  };

  const prepopulateFilterValues = (params: URLSearchParams) => {
    // Split URL query params
    const paramsArray: string[] = params.toString().split('&');
    const updatedSelect: Record<UserFilterTypes, Option[]> = initialFiltersState();

    // Search for matches between filter options and URL query params
    paramsArray.forEach((param) => {
      const [key, value] = param.split('=') as [key: string, value: string];

      switch (key) {
        case 'activationStates': {
          const selected: Option | undefined = activeStatusFilters?.find(
            (asf) => asf.key === value
          );
          if (selected) updatedSelect.activeStatus.push(selected);
          break;
        }
        case 'socialResponsesIds': {
          const selected = socialResponseFilterOptions.find(
            (socialResponse) => socialResponse.key === value
          );
          if (selected) updatedSelect.socialResponses.push(selected);
          break;
        }
        case 'taskStatus': {
          const selected: Option | undefined = taskStatusFilters?.find((tsf) => tsf.key === value);
          if (selected) updatedSelect.taskStatus.push(selected);
          break;
        }
        case 'shifts': {
          const selected = shiftsData?.find((shift) => shift.id === value);
          if (selected) updatedSelect.shifts.push({key: value, label: selected.name});
          break;
        }
        case 'patientOccurrences': {
          const selected = occurrencesTypes?.find((o) => o.occurrenceType.id === value);
          if (selected)
            updatedSelect.patientOccurrences.push({
              key: value,
              label: selected.occurrenceType.name,
            });
          break;
        }
        case 'generalOccurrences': {
          const selected = occurrencesTypes?.find((o) => o.occurrenceType.id === value);
          if (selected)
            updatedSelect.generalOccurrences.push({
              key: value,
              label: selected.occurrenceType.name,
            });
          break;
        }
        case 'badl': {
          const selected = activitiesSectorsAndGroups
            ?.find((sector) => sector.sector.id === BADL_SECTOR_ID)
            ?.groups?.find((group) => group.id === value);
          if (selected) updatedSelect.badl.push({key: value, label: selected.name});
          break;
        }
        case 'iadl': {
          const selected = activitiesSectorsAndGroups
            ?.find((sector) => sector.sector.id === IADL_SECTOR_ID)
            ?.groups?.find((group) => group.id === value);
          if (selected) updatedSelect.iadl.push({key: value, label: selected.name});
          break;
        }
        case 'clinicalSupport': {
          const selected = activitiesSectorsAndGroups
            ?.find((sector) => sector.sector.id === CLINICAL_SUPPORT_SECTOR_ID)
            ?.groups?.find((group) => group.id === value);
          if (selected) updatedSelect.clinicalSupport.push({key: value, label: selected.name});
          break;
        }
        case 'personalDevelopment': {
          const selected = activitiesSectorsAndGroups
            ?.find((sector) => sector.sector.id === PERSONAL_DEVELOPMENT_SECTOR_ID)
            ?.groups?.find((group) => group.id === value);
          if (selected) updatedSelect.personalDevelopment.push({key: value, label: selected.name});
          break;
        }
        default:
          // Do nothing
          break;
      }
    });

    // Commit to state
    setSelected(updatedSelect);
  };

  // Populate shifts section
  useEffect(() => {
    const shifts =
      shiftsData?.map<Option>((shift) => ({
        key: shift.id!,
        label: shift.name,
      })) ?? [];

    setFilters((x) => ({...x, shifts}));
  }, [shiftsData]);

  // Populate tasks section
  useEffect(() => {
    // If data is loading or there is an error, skip
    if (activitiesIsLoading || !activitiesSectorsAndGroups) return;

    const badl: Option[] = [];
    const iadl: Option[] = [];
    const clinicalSupport: Option[] = [];
    const personalDevelopment: Option[] = [];
    let badlName = '';
    let iadlName = '';
    let clinicalSupportName = '';
    let personalDevelopmentName = '';

    const permissions: {
      badl: boolean;
      iadl: boolean;
      clinicalSupport: boolean;
      personalDevelopment: boolean;
    } = (() => {
      const path = window.location.pathname;

      if (path.includes(ScreenPaths.Senior_CareAndActivity))
        return {
          badl: hasWritePermission(RolePermissionType.Seniors_CareAndActivities_BADL),
          iadl: hasWritePermission(RolePermissionType.Seniors_CareAndActivities_IADL),
          clinicalSupport: hasWritePermission(
            RolePermissionType.Seniors_CareAndActivities_ClinicalSupport
          ),
          personalDevelopment: hasWritePermission(
            RolePermissionType.Seniors_CareAndActivities_PersonalDevelopment
          ),
        };
      if (path.includes(ScreenPaths.Entity_Planner_Calendar))
        return {
          badl: hasWritePermission(RolePermissionType.Calendar_BADL),
          iadl: hasWritePermission(RolePermissionType.Calendar_IADL),
          clinicalSupport: hasWritePermission(RolePermissionType.Calendar_ClinicalSupport),
          personalDevelopment: hasWritePermission(RolePermissionType.Calendar_PersonalDevelopment),
        };

      return {
        badl: false,
        iadl: false,
        clinicalSupport: false,
        personalDevelopment: false,
      };
    })();

    for (const sector of activitiesSectorsAndGroups) {
      // Skip if there are no activity groups
      if (!sector.groups) {
        continue;
      }

      // Iterate
      const tasks = sector.groups.map((group) => ({
        key: group.id,
        label: group.name,
      }));

      switch (sector.sector.id) {
        case BADL_SECTOR_ID:
          if (!permissions.badl) break;
          badlName = sector.sector.name;
          badl.push(...tasks);
          break;
        case IADL_SECTOR_ID:
          if (!permissions.iadl) break;
          iadlName = sector.sector.name;
          iadl.push(...tasks);
          break;
        case CLINICAL_SUPPORT_SECTOR_ID:
          if (!permissions.clinicalSupport) break;
          clinicalSupportName = sector.sector.name;
          clinicalSupport.push(...tasks);
          break;
        case PERSONAL_DEVELOPMENT_SECTOR_ID:
          if (!permissions.personalDevelopment) break;
          personalDevelopmentName = sector.sector.name;
          personalDevelopment.push(...tasks);
          break;
        default:
          break;
      }
    }

    setFilters((x) => ({...x, badl, iadl, clinicalSupport, personalDevelopment}));
    setDynamicTitles({
      badl: badlName,
      iadl: iadlName,
      clinicalSupport: clinicalSupportName,
      personalDevelopment: personalDevelopmentName,
    });
  }, [activitiesSectorsAndGroups, activitiesIsLoading, hasWritePermission]);

  // Populate social responses section
  useEffect(() => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      socialResponses: socialResponseFilterOptions,
    }));
    setLoading(false);
  }, [socialResponseFilterOptions]);

  // Populate occurence types section
  useEffect(() => {
    const patients: Option[] = [];
    const general: Option[] = [];

    if (!occurrencesTypes) return;
    for (const occurence of occurrencesTypes) {
      const occurType = occurence.occurrenceType;

      // Skip inactive occurence types
      if (occurType.isActive === false) {
        continue;
      }

      // Add occurence type to the correct base
      if (occurType.category === 'patients') {
        patients.push({
          key: occurType.id,
          label: getLocalizedFieldValue(occurType, 'name'),
        });
      } else if (occurType.category === 'general') {
        general.push({
          key: occurType.id,
          label: getLocalizedFieldValue(occurType, 'name'),
        });
      }

      // Unkown occurence type category
      else {
        console.error('Unkown occurence type category: ', occurType.category);
      }
    }

    setFilters((x) => ({...x, patientOccurrences: patients, generalOccurrences: general}));
  }, [occurrencesTypes]);

  // Get filters query params on page load
  useEffect(() => {
    // Update hook fetch data endpoint
    if (queryParams) {
      // if empty, default to filtering per activation status as true
      if (queryParams.values.length === 0 && !hideActiveStatus)
        queryParams.set('activationStates', 'true');

      let locationQueryParams = queryParams.toString().split('&');

      // Filter the search param out
      const searchQueryParamIndex = locationQueryParams.findIndex((searchString) =>
        searchString.includes('search')
      );

      if (searchQueryParamIndex >= 0) locationQueryParams.splice(searchQueryParamIndex, 1);

      onChange(locationQueryParams.join('&'));

      prepopulateFilterValues(queryParams);
    }

    return () => {
      setSelected(initialFiltersState());
      setPendingSelected(initialFiltersState());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialResponseFilterOptions]);

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const hasAppliedFilters = useMemo(() => {
    return Object.values(selected).some((options) => options.length > 0);
  }, [selected]);

  const sortedFilterChips = useMemo(() => {
    return [
      ...Object.values(filterMenuOpen ? pendingSelected : selected).flatMap((opt) => opt),
    ].sort((a, b) => a.label.length - b.label.length);
  }, [filterMenuOpen, pendingSelected, selected]);

  const chipsLimit = useMemo(() => {
    let allChipsLen = 0;
    let firstTwoChipsLen = 0;

    for (let i = 0; i < 3; i++) {
      const chip = sortedFilterChips[i];

      if (!chip) break;
      if (i === 0) {
        firstTwoChipsLen = chip.label.length;
      }
      if (i === 1) {
        firstTwoChipsLen += chip.label.length;
      }

      allChipsLen += chip.label.length;
    }

    // 3 chips display: is the max.
    // 2 chips display: if the first two chips are too long.
    // 1 chip display: if the first chip is too long.
    let chipsLimit = allChipsLen <= CHIPS_TEXT_MAX_LIMIT.all ? 3 : 2;
    if (chipsLimit < 3) {
      chipsLimit = firstTwoChipsLen < CHIPS_TEXT_MAX_LIMIT.firstTwo ? chipsLimit : 1;
    }

    return chipsLimit;
  }, [sortedFilterChips]);

  const loadingContainerSx: SxProps<Theme> = {placeSelf: 'center', placeContent: 'center'};

  const displayWrapperSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    width: '225px',
    height: '40px',
    borderRadius: '5px',
    padding: '5px 5px',
    overflow: 'hidden',
    border: filterMenuOpen ? '1px solid var(--dcm-blue-base)' : '1px solid var(--dcm-blue-lighter)',
    background: filterMenuOpen ? 'var(--dcm-bgseniormenu)' : 'white',
    gap: '0px',
    cursor: 'pointer',
    ':hover': {
      background: 'var(--dcm-bgseniormenu)',
      border: '1px solid var(--dcm-blue-base)',
    },
  };

  const displayContainerSx: SxProps<Theme> = {
    position: 'relative',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    overflow: 'hidden',
    gap: '4px',
  };

  const displayIconWrapper: SxProps<Theme> = {
    height: '22px',
    width: '22px',
    right: 0,
  };

  const menuSx: SxProps<Theme> = {
    mt: '5px',
    '.MuiMenu-paper': {
      borderRadius: '4px',
      boxShadow: '0px 1px 3px 1px rgba(60, 64, 67, 0.15), 0px 1px 2px 0px rgba(60, 64, 67, 0.30)',

      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        width: '0px',
      },
    },
  };

  const menuListSx: SxProps<Theme> = {
    minWidth: '220px',
    maxWidth: '340px',
    minHeight: '200px',
    padding: '14px 8px 14px 14px',
    display: 'grid',
    placeContent: isLoading ? 'center' : 'flex-start',
    gridTemplateColumns: '1fr',
  };

  const filtersSectionsWrapperSx: SxProps<Theme> = {
    overflowY: 'auto',
    maxHeight: '345px',
    gap: 0,
  };

  const filterMenuHeaderSx: SxProps<Theme> = {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '32px',
  };

  const filtersTitleSx: SxProps<Theme> = {
    color: 'var(--dcm-darkgray)',
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '10px',
  };

  const filtersClearAllLabel: SxProps<Theme> = {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.175px',
    padding: 0,
    ':hover': {
      filter: 'brightness(90%)',
    },
  };

  const accordionSx: SxProps<Theme> = {
    boxShadow: 'unset',
    '&.Mui-expanded': {
      margin: 0,
    },
    '.MuiAccordionSummary-root': {
      height: 'calc(26px + 6px + 6px)',
      minHeight: 'fit-content',
      padding: '6px 2px',
      borderBottom: '1px solid var(--dcm-gray-lightbg)',
      '&.Mui-expanded': {
        minHeight: 'fit-content',
      },
    },
    '.MuiAccordionSummary-content': {
      margin: 0,
      '&.Mui-expanded': {
        margin: 0,
      },
    },
    '&.MuiAccordion-root:before': {
      display: 'none',
    },
    '.MuiAccordionSummary-expandIconWrapper': {
      height: '24px',
      width: '24px',
      marginRight: '12px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '&.Mui-expanded': {
        transform: 'rotate(90deg)',
      },
    },
    '.MuiAccordionDetails-root': {
      mt: 0.5,
      padding: 0,
    },
  };

  const filterActionWrapperSx: SxProps<Theme> = {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    position: 'sticky',
    background: 'white',
    bottom: 0,
    paddingRight: '6px',
  };

  const filterActionContainerSx: SxProps<Theme> = {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  };

  const filterTypography: SxProps<Theme> = {
    color: 'var(--dcm-gray)',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
  };

  const filterFormControl: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '32px',
    padding: '10px 8px',
    gap: 1,
  };

  const filterLabelSx: SxProps<Theme> = {
    color: 'var(--dcm-darkgray)',
    textAlign: 'start center',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '14px',
    cursor: 'pointer',
  };

  const filterChipLimit: SxProps<Theme> = {
    color: 'var(--dcm-blue-base)',
    fontSize: '15px',
    fontWeight: 600,
  };

  const gradientBoxSx: SxProps<Theme> = {
    width: '100%',
    height: '30px',
    position: 'absolute',
    top: '-30px',
    background: 'linear-gradient(to bottom, transparent, white)',
    pointerEvents: 'all',
  };

  return (
    <>
      {/**
       * Display/Trigger
       */}
      {isRunningCloseToMobile ? (
        <Button onClick={openFiltersMenu} variant="inverted">
          <FilterAltOutlined
            htmlColor={hasAppliedFilters ? 'var(--dcm-blue-base)' : 'var(--dcm-gray-general)'}
          />
        </Button>
      ) : (
        <Stack onClick={openFiltersMenu} sx={displayWrapperSx}>
          <Box sx={displayContainerSx}>
            {sortedFilterChips.map((entry, index) => {
              const reactKey = `entry_${entry?.key}_${index}`;

              if (index < chipsLimit)
                return (
                  <Chip
                    key={reactKey}
                    label={entry?.label}
                    size="small"
                    deleteIcon={<Close htmlColor="var(--dcm-white-base)" />}
                    onDelete={() => handleDeleteChip(entry.key)}
                    style={{maxWidth: sortedFilterChips.length > 1 ? '160px' : '185px'}}
                    sx={{
                      borderRadius: '5px',
                      backgroundColor: 'var(--dcm-blue-base)',
                      color: 'white',
                      '.MuiChip-deleteIcon': {
                        color: 'white!important',
                      },
                      '.MuiChip-label': {
                        paddingRight: '5px',
                        paddingLeft: '5px',
                      },
                    }}
                  />
                );

              return <Fragment key={reactKey} />;
            })}

            {sortedFilterChips.length > chipsLimit && (
              <Typography
                sx={filterChipLimit}
              >{`+ ${sortedFilterChips.length - chipsLimit}`}</Typography>
            )}
          </Box>
          <IconButton sx={displayIconWrapper}>
            <FilterAltOutlined
              htmlColor={hasAppliedFilters ? 'var(--dcm-blue-base)' : 'var(--dcm-gray-general)'}
            />
          </IconButton>
        </Stack>
      )}

      {/**
       * Options menu
       */}
      <Menu
        id="user-filters-menu"
        anchorEl={anchorEl}
        open={filterMenuOpen}
        onClose={closeFilterMenu}
        MenuListProps={{
          'aria-labelledby': 'user-filters-button',
          sx: menuListSx,
        }}
        sx={menuSx}
        disablePortal
      >
        <Stack>
          {isLoading ? (
            <CircularProgress sx={loadingContainerSx} />
          ) : (
            <Stack gap={2}>
              <Stack sx={filterMenuHeaderSx}>
                <Typography sx={filtersTitleSx}>
                  {t(translations.office.users_filters.title)}
                </Typography>
                <Typography
                  onClick={sortedFilterChips.length > 0 ? () => clearFilters() : () => {}}
                  sx={{
                    ...filtersClearAllLabel,
                    color:
                      sortedFilterChips.length > 0
                        ? 'var(--dcm-blue-base)'
                        : 'var(--dcm-gray-general)',
                    cursor: sortedFilterChips.length > 0 ? 'pointer' : 'not-allowed',
                  }}
                >
                  {t(translations.office.users_filters.clear_all)}
                </Typography>
              </Stack>

              <Stack sx={filtersSectionsWrapperSx}>
                {Object.entries(filters).map(([section, filterOptions]) => {
                  const reactKey = `filter_accordion_${section}`;

                  if (filterOptions.length === 0) return <Fragment key={reactKey} />;

                  return (
                    <Accordion key={reactKey} sx={accordionSx} defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ArrowForwardIosSharp sx={{fontSize: '0.9rem'}} />}
                      >
                        <Typography sx={filterTypography}>
                          {t(
                            // @ts-ignore
                            translations.office.users_filters.sections[section as UserFilterTypes]
                              ?.title ??
                              dynamicTitles[section as UserFilterTypes] ??
                              section
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {filterOptions.map((option) => {
                          const reactKey = `option_${option.key}`;

                          const checked = pendingSelected[section as UserFilterTypes]
                            ?.map((option) => option.key)
                            ?.includes(option.key);

                          return (
                            <FormControl key={reactKey} sx={filterFormControl}>
                              <CustomCheckbox
                                id={`checkbox_sr_${option.key}`}
                                size="small"
                                checked={checked}
                                onChange={(_event, checked) => {
                                  let newUpdated = {
                                    ...pendingSelected,
                                  };

                                  if (checked) {
                                    newUpdated = {
                                      ...newUpdated,
                                      [section]: [
                                        ...newUpdated[section as UserFilterTypes],
                                        option,
                                      ],
                                    };
                                  } else {
                                    newUpdated = {
                                      ...newUpdated,
                                      [section]: [...newUpdated[section as UserFilterTypes]].filter(
                                        (opt) => opt.key !== option.key
                                      ),
                                    };
                                  }

                                  setPendingSelected(newUpdated);
                                }}
                                sx={{padding: '0px!important'}}
                              />
                              <Typography
                                component="label"
                                htmlFor={`checkbox_sr_${option.key}`}
                                sx={filterLabelSx}
                              >
                                {option.label}
                              </Typography>
                            </FormControl>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>

              <Stack sx={filterActionWrapperSx}>
                <Box sx={gradientBoxSx} />
                <Stack sx={filterActionContainerSx}>
                  <Button color="error" onClick={closeFilterMenu}>
                    {t(translations.office.generics.cancel)}
                  </Button>
                  <Button onClick={applyFilters}>{t(translations.office.generics.apply)}</Button>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Menu>
    </>
  );
};
